/*
  Первый шаг мастера «Новый лицевой счёт» (20.10.2026): номер, адрес и сведения о помещении.

  Решение владельца: заказчик может начать работу без выгрузки. Номер и адрес правятся
  позже на карточке (21.10.2026, `AccountEditButton`), пока по счёту нет документов: на них
  держатся история счёта и узнавание счёта обменом с 1С, поэтому форма говорит об этом
  заранее, а не после ошибки. Остальное (должники,
  начисления, оплаты, проживающие) вносится следующими шагами и позже на карточке.

  Границы значений проверяет сервер (те же, что у загрузки сведений о помещениях);
  отказ показывается здесь, под формой, словами и с советом.
*/

import { useEffect, useState } from 'react'

import { api } from '../../api/client.js'
import { PREMISES_TYPE_TITLES } from '../../api/format.js'
import { parseCount } from '../../api/parseCount.js'
import { Field, Notice } from '../Common.jsx'
import Dropdown from '../Select.jsx'

const START = {
  number: '',
  address: '',
  premises_type: '',
  area: '',
  living_area: '',
  rooms: '',
  floor: '',
}

function body(form) {
  const result = { number: form.number.trim(), address: form.address.trim() }
  if (form.premises_type) result.premises_type = form.premises_type
  for (const name of ['area', 'living_area']) {
    const value = form[name].trim().replace(',', '.')
    if (value !== '') result[name] = value
  }
  for (const name of WHOLE_FIELDS) {
    const { value } = parseCount(form[name])
    if (value !== null) result[name] = value
  }
  return result
}

/* Опечатка в «Комнат» и «Этаж» не уходит на сервер пустотой: причина - под полем (28.10.2026). */
const WHOLE_FIELDS = ['rooms', 'floor']

function wholeProblems(form) {
  const problems = {}
  for (const name of WHOLE_FIELDS) {
    const { error } = parseCount(form[name])
    if (error) problems[name] = error
  }
  return problems
}

export default function AccountForm({ onCreated, onDirty }) {
  const [form, setForm] = useState(START)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const set = (name) => (event) => setForm({ ...form, [name]: event.target.value })

  // Страница спрашивает «уйти без сохранения?», пока в форме что-то набрано (аудит 28.10.2026).
  const typed = form.number.trim() !== '' || form.address.trim() !== ''
  useEffect(() => {
    onDirty?.(typed)
  }, [typed, onDirty])

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onCreated(await api.createAccount(body(form)))
    } catch (failure) {
      setError(failure)
      setBusy(false)
    }
  }

  const problems = wholeProblems(form)
  const valid =
    form.number.trim() !== '' &&
    form.address.trim().length >= 5 &&
    Object.keys(problems).length === 0

  return (
    <form className="section card" onSubmit={submit}>
      <h2 className="section__title">Шаг 1. Помещение</h2>
      <div className="muted">
        Номер и адрес можно будет поправить кнопкой «Изменить данные» на карточке счёта, пока по
        нему нет документов: на них держатся история счёта и обмен с учётной системой.
      </div>
      <div className="row">
        <Field label="Номер лицевого счёта" width="s">
          <input className="field__input" value={form.number} maxLength={64} onChange={set('number')} />
        </Field>
        <Field label="Адрес помещения" width="l">
          <input
            className="field__input"
            value={form.address}
            maxLength={500}
            onChange={set('address')}
            placeholder="ул. Демонстрационная, д. 1, кв. 3"
          />
        </Field>
      </div>
      <div className="row">
        <div className="field field--l">
          <span className="field__label" id="account-premises-label">
            Вид собственности
          </span>
          <Dropdown
            value={form.premises_type}
            onChange={(premises_type) => setForm({ ...form, premises_type })}
            options={PREMISES_TYPE_TITLES}
            empty="не указан"
            labelledBy="account-premises-label"
          />
        </div>
        <Field label="Общая площадь, кв. м" width="s">
          <input className="field__input" inputMode="decimal" value={form.area} onChange={set('area')} />
        </Field>
        <Field label="Жилая, кв. м" width="s">
          <input
            className="field__input"
            inputMode="decimal"
            value={form.living_area}
            onChange={set('living_area')}
          />
        </Field>
        <Field label="Комнат" width="xs" error={problems.rooms}>
          <input
            className="field__input"
            inputMode="numeric"
            value={form.rooms}
            aria-invalid={problems.rooms ? 'true' : undefined}
            onChange={set('rooms')}
          />
        </Field>
        <Field label="Этаж" width="xs" error={problems.floor}>
          <input
            className="field__input"
            inputMode="numeric"
            value={form.floor}
            aria-invalid={problems.floor ? 'true' : undefined}
            onChange={set('floor')}
          />
        </Field>
      </div>
      <Notice error={error} />
      <div className="row row--tight">
        <button className="button" type="submit" disabled={busy || !valid}>
          Завести счёт и перейти к должникам
        </button>
      </div>
    </form>
  )
}
